import { Link } from 'react-router-dom';
import BlueDotsBackground from '../../BlueDotsBackground';

const automation = [
  {
    iconClass: 'fas fa-balance-scale',
    title: 'G/L Reconciliation',
    description:
      'OpenPharma seamlessly integrates data from multiple systems and organizes it to fit your unique reconciliation requirements. Quickly identify and resolve transaction-level discrepancies, reduce financial risk, accelerate month-end close, and enhance audit readiness.',
  },
  {
    iconClass: 'fas fa-chart-line',
    title: 'Accrual Life Cycle Monitoring',
    description:
      'Automatically track how accruals build, age, and settle over time with clear visualizations of historical trends. No more manual tracking across scattered systems.',
  },
  {
    iconClass: 'fas fa-lightbulb',
    title: 'Forecasting Made Easy',
    description:
      'Clear visibility into reserve trends to inform GTN forecasting decisions, eliminating manual aggregation and streamlining planning with easy-to-read reserve data.',
  },
];

const benefits = [
  {
    iconClass: 'fas fa-eye',
    title: 'Data Visibility',
    description:
      'Consolidate GTN activity into a single, easy-to-navigate platform. No more digging through spreadsheets or emailing for updates — full transparency across periods, products, and channels.',
  },
  {
    iconClass: 'fas fa-tachometer-alt',
    title: 'Data That Scales',
    description:
      'With pre-aggregated data and high-performance processing, you can answer key business questions in seconds, even when working with millions of rows.',
  },
];

const differentiators = [
  {
    iconClass: 'fas fa-magnifying-glass',
    title: 'Full Transparency',
    description:
      'OpenPharma creates clear, auditable trails for all data flowing into the system, supporting reserve tracking, policy alignment, and full SOX and audit compliance.',
  },
  {
    iconClass: 'fas fa-database',
    title: 'Decision-Ready Data',
    description:
      "Deliver decision-ready, clean, centralized data that's easy to transform into intuitive dashboards and reports — enabling faster understanding and decision-making.",
  },
];

export default function GtnForecasting() {
  return (
    <>
      <BlueDotsBackground />
      <section
        id="hero"
        className="relative text-center text-white overflow-hidden font-sans"
      >
        <div className="relative z-10 px-6 min-h-screen flex flex-col justify-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight leading-tight mb-8">
              Struggling to track and forecast GTN reserves?
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 font-light mb-12">
              Kernel simplifies GTN management by providing automated reserve
              tracking, full transparency, and real-time forecasting insights.
            </p>
            <div className="max-w-xl w-full mx-auto mb-20"></div>
          </div>
        </div>
      </section>

      <section id="automation" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Automated End to End Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {automation.map((lifecycle, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i
                  className={`${lifecycle.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {lifecycle.title}
                </h3>
                <p className="text-gray-300 text-sm">{lifecycle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="benefits" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i className={`${benefit.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="differentiators"
        className="py-16 px-6 text-white text-center"
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            What Sets Us Apart
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {differentiators.map((differentiator, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i
                  className={`${differentiator.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {differentiator.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {differentiator.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="cta" className="py-16 px-6 text-white text-center">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center gap-6">
          <Link to="/contact">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Contact Us
            </button>
          </Link>
          <Link to="/login">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Explore Our Platform
            </button>
          </Link>
        </div>
      </section>
    </>
  );
}
